import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Star, Gift, Percent, Save } from "lucide-react";
import { PlanGate } from "@/components/enterprise-gate";
import { readJsonArray } from "@/lib/http";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
const QK = ["customers"];

function LoyaltyContent() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [editId, setEditId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ discountRate: "", loyaltyPoints: "" });

  const { data: customers, isLoading } = useQuery({
    queryKey: QK,
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/customers`, { credentials: "include" });
      return readJsonArray<any>(res, "customers");
    },
  });

  const update = useMutation({
    mutationFn: async ({ id, ...data }: { id: number; discountRate: number; loyaltyPoints: number }) => {
      const res = await fetch(`${BASE}/api/customers/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as any).error || "Failed to update customer");
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK });
      setEditId(null);
    },
    onError: (e: Error) => toast({ title: e.message, variant: "destructive" }),
  });

  const openEdit = (c: any) => {
    setEditForm({ discountRate: String(c.discountRate), loyaltyPoints: String(c.loyaltyPoints) });
    setEditId(c.id);
  };

  const totalPoints = customers?.reduce((s: number, c: any) => s + c.loyaltyPoints, 0) ?? 0;
  const withDiscount = customers?.filter((c: any) => c.discountRate > 0) ?? [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-muted-foreground">Total Customers</span>
              <Star className="w-4 h-4 text-amber-500" />
            </div>
            <div className="text-2xl font-bold">{customers?.length ?? 0}</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-muted-foreground">With Active Discount</span>
              <Percent className="w-4 h-4 text-emerald-500" />
            </div>
            <div className="text-2xl font-bold">{withDiscount.length}</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-muted-foreground">Total Loyalty Points</span>
              <Gift className="w-4 h-4 text-purple-500" />
            </div>
            <div className="text-2xl font-bold">{totalPoints.toLocaleString()}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle>Customer Loyalty Settings</CardTitle></CardHeader>
        <CardContent className="pt-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead className="text-right">Discount Rate</TableHead>
                  <TableHead className="text-right">Loyalty Points</TableHead>
                  <TableHead className="w-[80px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {!customers?.length ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No customers yet</TableCell></TableRow>
                ) : customers.map((c: any) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell className="text-muted-foreground">{c.phone || "—"}</TableCell>
                    <TableCell className="text-right">
                      {c.discountRate > 0
                        ? <Badge className="bg-emerald-100 text-emerald-800">{c.discountRate}%</Badge>
                        : <span className="text-muted-foreground text-sm">None</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      {c.loyaltyPoints > 0
                        ? <Badge variant="outline"><Star className="w-3 h-3 mr-1 inline" />{c.loyaltyPoints}</Badge>
                        : <span className="text-muted-foreground text-sm">0</span>}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => openEdit(c)}>Edit</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={editId !== null} onOpenChange={o => !o && setEditId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Edit Loyalty Settings</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Discount Rate (%)</label>
              <Input type="number" step="0.5" min="0" max="100" value={editForm.discountRate}
                onChange={e => setEditForm(p => ({ ...p, discountRate: e.target.value }))} className="mt-1" placeholder="0" />
              <p className="text-xs text-muted-foreground mt-1">Applied automatically at checkout for this customer.</p>
            </div>
            <div>
              <label className="text-sm font-medium">Loyalty Points</label>
              <Input type="number" min="0" value={editForm.loyaltyPoints}
                onChange={e => setEditForm(p => ({ ...p, loyaltyPoints: e.target.value }))} className="mt-1" placeholder="0" />
            </div>
            <Button className="w-full gap-2" disabled={update.isPending}
              onClick={() => editId && update.mutate({ id: editId, discountRate: Number(editForm.discountRate), loyaltyPoints: Number(editForm.loyaltyPoints) })}>
              {update.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Settings
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function PeopleLoyalty() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Loyalty & Discounts</h1>
        <p className="text-muted-foreground mt-1">Manage customer loyalty points and discount rates.</p>
      </div>
      <PlanGate allowedPlans={["pro", "enterprise", "trial"]} feature="Loyalty & Discounts">
        <LoyaltyContent />
      </PlanGate>
    </div>
  );
}
